import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { useLiveRefresh } from "@/contexts/LiveRefresh";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Edit2,
    Plus,
    Trash2,
    Loader2,
    Building2,
    FileSpreadsheet,
    ChevronDown,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ClientFormDialog from "@/components/ClientFormDialog";
import { toast } from "sonner";

export default function Clients() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [toDelete, setToDelete] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/clients");
            setClients(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);
    useLiveRefresh(load);

    const openNew = () => {
        setEditing(null);
        setShowForm(true);
    };

    const openEdit = (c) => {
        setEditing(c);
        setShowForm(true);
    };

    const onDelete = async () => {
        if (!toDelete) return;
        try {
            await api.delete(`/clients/${toDelete.id}`);
            toast.success("Client deleted");
            setToDelete(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const exportClients = (format) => {
        const token = localStorage.getItem("sdc_token");
        const params = new URLSearchParams({ format, auth: token || "" });
        window.location.href = `${API}/exports/clients?${params.toString()}`;
    };

    return (
        <div className="space-y-6" data-testid="clients-page">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Directory
                    </div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                        Clients
                    </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {isAdmin && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" data-testid="clients-export-trigger">
                                    <FileSpreadsheet className="mr-1.5 h-4 w-4" />
                                    Export
                                    <ChevronDown className="ml-1 h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onClick={() => exportClients("xlsx")}
                                    data-testid="clients-export-xlsx"
                                >
                                    Excel (.xlsx)
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => exportClients("csv")}
                                    data-testid="clients-export-csv"
                                >
                                    CSV (.csv)
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    <Button onClick={openNew} data-testid="new-client-button">
                        <Plus className="mr-1.5 h-4 w-4" />
                        Add client
                    </Button>
                </div>
            </div>

            <Card className="sdc-card overflow-hidden">
                {loading ? (
                    <div className="grid place-items-center py-20">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                ) : clients.length === 0 ? (
                    <div className="py-16 text-center">
                        <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
                        <p className="mt-3 text-sm text-muted-foreground">
                            No clients yet. They are auto-created when you add a task, or add one
                            here.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                                    <TableHead>Name</TableHead>
                                    <TableHead>Contact person</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead>GSTIN</TableHead>
                                    <TableHead className="text-right pr-6">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {clients.map((c) => (
                                    <TableRow
                                        key={c.id}
                                        className="sdc-table-row"
                                        data-testid={`client-row-${c.id}`}
                                    >
                                        <TableCell className="font-semibold">{c.name}</TableCell>
                                        <TableCell>{c.contact_person || "—"}</TableCell>
                                        <TableCell>{c.email || "—"}</TableCell>
                                        <TableCell>{c.phone || "—"}</TableCell>
                                        <TableCell className="font-mono text-xs">
                                            {c.gstin || "—"}
                                        </TableCell>
                                        <TableCell className="text-right pr-4">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => openEdit(c)}
                                                    data-testid={`client-edit-${c.id}`}
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => setToDelete(c)}
                                                    data-testid={`client-delete-${c.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-600" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            <ClientFormDialog
                open={showForm}
                onOpenChange={setShowForm}
                client={editing}
                onSaved={() => load()}
            />

            <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this client?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Tasks linked to this client will keep the client name. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
